// components/ui/Select.jsx - Shadcn style Select
import React, { useState, useRef, useCallback } from "react";
import * as Popover from "@radix-ui/react-popover";
import { ChevronDown, Check, Search, X } from "lucide-react";
import { useTranslation } from "react-i18next";
import { cn } from "../../utils/cn";
import Button from "./Button";
import Input from "./Input";

// Unicode bidi isolates, written as code points so they can never be "tidied away" as invisible
// whitespace by an editor or a lint autofix. FIRST STRONG ISOLATE / POP DIRECTIONAL ISOLATE.
const BIDI_FSI = String.fromCharCode(0x2068);
const BIDI_PDI = String.fromCharCode(0x2069);

const Select = React.forwardRef(({
    className,
    options = [],
    value,
    defaultValue,
    placeholder,
    multiple = false,
    disabled = false,
    required = false,
    label,
    description,
    error,
    searchable = false,
    clearable = false,
    loading = false,
    id,
    name,
    onChange,
    onOpenChange,
    ...props
}, ref) => {
    const { t } = useTranslation();
    const [isOpen, setIsOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");
    const searchInputRef = useRef(null);

    // Generate unique ID if not provided
    const selectId = id || `select-${Math.random()?.toString(36)?.substr(2, 9)}`;

    // Filter options based on search
    const filteredOptions = searchable && searchTerm
        ? options?.filter(option =>
            option?.label?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
            (option?.value && option?.value?.toString()?.toLowerCase()?.includes(searchTerm?.toLowerCase()))
        )
        : options;

    // Effective placeholder: when the first option is an empty-value "All"/"Any" default, show its
    // label as the resting text (e.g. "All departments") instead of a generic prompt.
    const resolvedPlaceholder = placeholder
        ?? (options?.[0]?.value === '' ? options[0].label : t('Select an option'));

    // Get selected option(s) for display
    const getSelectedDisplay = () => {
        if (!value) return resolvedPlaceholder;

        if (multiple) {
            const selectedOptions = options?.filter(opt => value?.includes(opt?.value));
            if (selectedOptions?.length === 0) return resolvedPlaceholder;
            if (selectedOptions?.length === 1) return selectedOptions?.[0]?.label;
            // The count is BIDI-ISOLATED (U+2068 FSI … U+2069 PDI) rather than interpolated bare.
            // In an RTL page a bare Latin digit next to Arabic text is reordered by the bidi
            // algorithm and the Arabic summary rendered with the number stranded on the wrong
            // side. The isolate pins it. `count` is still passed as a NUMBER so i18next can pick
            // the plural form (ar has six); `countText` is what the strings actually print.
            const n = selectedOptions?.length;
            return t('itemsSelected', {
                count: n,
                countText: `${BIDI_FSI}${n}${BIDI_PDI}`,
                defaultValue: '{{countText}} items selected',
            });
        }

        const selectedOption = options?.find(opt => opt?.value === value);
        return selectedOption ? selectedOption?.label : resolvedPlaceholder;
    };

    // Centralised open/close so external onOpenChange + search reset stay in sync,
    // whether the change is driven by Radix (trigger/outside/Escape) or by us (selection).
    const setOpen = (open) => {
        if (disabled) return;
        setIsOpen(open);
        onOpenChange?.(open);
        if (!open) setSearchTerm("");
    };

    const handleOptionSelect = (option) => {
        if (multiple) {
            const newValue = value || [];
            const updatedValue = newValue?.includes(option?.value)
                ? newValue?.filter(v => v !== option?.value)
                : [...newValue, option?.value];
            onChange?.(updatedValue);
        } else {
            onChange?.(option?.value);
            setOpen(false);
        }
    };

    const handleClear = (e) => {
        e?.stopPropagation();
        onChange?.(multiple ? [] : '');
    };

    const handleSearchChange = (e) => {
        setSearchTerm(e?.target?.value);
    };

    const isSelected = (optionValue) => {
        if (multiple) {
            return value?.includes(optionValue) || false;
        }
        return value === optionValue;
    };

    const hasValue = multiple ? value?.length > 0 : value !== undefined && value !== '';

    const allSelected = multiple && filteredOptions?.length > 0 && filteredOptions?.every(opt => value?.includes(opt?.value));

    const handleSelectAll = () => {
        if (allSelected) {
            // Deselect all filtered options (keep any that aren't in the filtered set)
            const filteredValues = new Set(filteredOptions?.map(opt => opt?.value));
            const remaining = (value || []).filter(v => !filteredValues.has(v));
            onChange?.(remaining);
        } else {
            // Select all filtered options (merge with existing)
            const filteredValues = filteredOptions?.map(opt => opt?.value) || [];
            const merged = [...new Set([...(value || []), ...filteredValues])];
            onChange?.(merged);
        }
    };

    // Mouse-wheel scrolling fix for when the dropdown is rendered inside a Radix
    // modal Dialog. The Dialog wraps its content in react-remove-scroll, which
    // adds a bubble-phase `wheel` listener on `document` and cancels (preventDefault)
    // any wheel event whose target is outside the dialog's locked subtree. Our
    // dropdown is portalled to <body> (Radix Popover), so its wheel events get
    // cancelled and the list can't be scrolled with the wheel (only the scrollbar
    // works). Attaching a native listener directly on the list and calling
    // stopPropagation lets the event resolve here — before it reaches that
    // document-level listener — so native overflow scrolling proceeds. Outside a
    // dialog there is no such listener, so this is a harmless no-op.
    const stopWheelPropagation = useCallback((e) => {
        e.stopPropagation();
    }, []);
    const listWheelNodeRef = useRef(null);
    const setListWheelRef = useCallback((node) => {
        if (listWheelNodeRef.current) {
            listWheelNodeRef.current.removeEventListener('wheel', stopWheelPropagation);
        }
        listWheelNodeRef.current = node;
        if (node) {
            node.addEventListener('wheel', stopWheelPropagation, { passive: true });
        }
    }, [stopWheelPropagation]);

    // A combobox with no visible <label> reached screen readers as an unnamed button ("button-name"
    // in axe). Every Select already knows what it is from its label or its placeholder, so name the
    // trigger from those rather than asking ~100 call sites to repeat themselves.
    const triggerAriaLabel = props['aria-label']
        ?? (typeof label === 'string' ? label : undefined)
        ?? (typeof resolvedPlaceholder === 'string' ? resolvedPlaceholder : undefined);

    return (
        <div className={cn("relative", className)}>
            {label && (
                <label
                    htmlFor={selectId}
                    className={cn(
                        "text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 mb-2 block",
                        error ? "text-destructive" : "text-foreground"
                    )}
                >
                    {label}
                    {required && <span className="text-destructive ms-1">*</span>}
                </label>
            )}
            <div className="relative">
                {/* Radix Popover handles layering, focus management (pauses parent
                    Dialog focus traps), dismissal, and collision-aware positioning so
                    the dropdown works correctly inside modals and never overflows the
                    viewport. */}
                <Popover.Root open={isOpen} onOpenChange={setOpen}>
                    <Popover.Trigger asChild>
                        <button
                            ref={ref}
                            id={selectId}
                            type="button"
                            className={cn(
                                "flex h-10 w-full items-center justify-between rounded-md border border-input bg-background text-foreground px-3 py-2 text-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary transition-colors disabled:cursor-not-allowed disabled:opacity-50",
                                error && "border-destructive focus-visible:ring-destructive/50",
                                !hasValue && "text-muted-foreground"
                            )}
                            disabled={disabled}
                            aria-invalid={error ? true : undefined}
                            aria-describedby={error ? `${selectId}-error` : undefined}
                            role="combobox"
                            aria-haspopup="listbox"
                            aria-expanded={isOpen}
                            {...props}
                            aria-label={triggerAriaLabel}
                        >
                            <span className="truncate">{getSelectedDisplay()}</span>

                            <div className="flex items-center gap-1">
                                {loading && (
                                    <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                                    </svg>
                                )}

                                {clearable && hasValue && !loading && (
                                    <Button
                                        variant="ghost"
                                        size="icon"
                                        className="h-4 w-4"
                                        onClick={handleClear}
                                    >
                                        <X className="h-3 w-3" />
                                    </Button>
                                )}

                                <ChevronDown className={cn("h-4 w-4 transition-transform", isOpen && "rotate-180")} />
                            </div>
                        </button>
                    </Popover.Trigger>

                    <Popover.Portal>
                        <Popover.Content
                            align="start"
                            sideOffset={4}
                            collisionPadding={8}
                            role="listbox"
                            aria-multiselectable={multiple ? 'true' : undefined}
                            onOpenAutoFocus={searchable ? (e) => {
                                // Keep focus on the search field instead of the first option.
                                e.preventDefault();
                                searchInputRef.current?.focus();
                            } : undefined}
                            className="z-[9999] flex flex-col overflow-hidden bg-popover text-popover-foreground border border-border rounded-md shadow-md"
                            style={{
                                width: 'var(--radix-popover-trigger-width)',
                                maxHeight: 'var(--radix-popover-content-available-height)',
                            }}
                        >
                            {searchable && (
                                <div className="p-2 border-b shrink-0">
                                    <div className="relative">
                                        <Search className="absolute start-2 top-2.5 h-4 w-4 text-muted-foreground pointer-events-none" />
                                        <Input
                                            ref={searchInputRef}
                                            placeholder={t('searchOptions', { defaultValue: 'Search options...' })}
                                            value={searchTerm}
                                            onChange={handleSearchChange}
                                            className="ps-8"
                                        />
                                    </div>
                                </div>
                            )}

                            <div ref={setListWheelRef} className="py-1 overflow-y-auto overscroll-contain min-h-0">
                                {multiple && filteredOptions?.length > 0 && (
                                    <>
                                        <div
                                            role="option"
                                            aria-selected={allSelected}
                                            className={cn(
                                                "relative flex min-h-[36px] cursor-pointer select-none items-center gap-2 rounded-sm px-3 py-2 text-sm outline-none hover:bg-accent hover:text-accent-foreground font-medium",
                                                allSelected && "bg-primary text-primary-foreground"
                                            )}
                                            onClick={handleSelectAll}
                                        >
                                            <span className="flex-1">{allSelected ? t('Deselect All') : t('Select All')}</span>
                                            {allSelected && <Check className="h-4 w-4" />}
                                        </div>
                                        <div className="border-b border-border mx-2" />
                                    </>
                                )}
                                {filteredOptions?.length === 0 ? (
                                    <div className="px-3 py-2 text-sm text-muted-foreground">
                                        {searchTerm ? t('noOptionsFound') : t('noOptionsAvailable')}
                                    </div>
                                ) : (
                                    filteredOptions?.map((option) => (
                                        <div
                                            key={option?.value}
                                            role="option"
                                            aria-selected={isSelected(option?.value)}
                                            aria-disabled={option?.disabled || undefined}
                                            className={cn(
                                                // The row must GROW with its content. It used to be a single
                                                // `items-center` flex line holding the label, the checkmark AND the
                                                // meta/description text: a label long enough to wrap kept the row at
                                                // its one-line height, so the meta line was painted straight over the
                                                // second line of the label (and over the checkmark). `min-h-[36px]` keeps
                                                // every single-line row at exactly the height it had before, while
                                                // `items-start` + a stacked text column lets a wrapped label push the
                                                // row taller instead of overlapping.
                                                "relative flex min-h-[36px] cursor-pointer select-none items-start gap-2 rounded-sm px-3 py-2 text-sm outline-none hover:bg-accent hover:text-accent-foreground",
                                                isSelected(option?.value) && "bg-primary text-primary-foreground",
                                                option?.disabled && "pointer-events-none opacity-50"
                                            )}
                                            onClick={() => !option?.disabled && handleOptionSelect(option)}
                                        >
                                            <span className="flex-1 min-w-0 break-words">
                                                {option?.label}
                                                {option?.description && (
                                                    // NOT `text-xs`. `assets/styles/utilities.css` redefines `.text-xs` with
                                                    // `line-height: 1.2` and is loaded AFTER Tailwind, so it also beats any
                                                    // `leading-*` utility placed next to it. A 12px line box of 14.4px is
                                                    // shorter than Arabic glyph extents (Segoe UI Arabic ascent+descent is
                                                    // ~1.3em, marks reach higher): the ink spilled below the row, where the
                                                    // NEXT row's highlight painted over it, or where light-on-white text was
                                                    // simply invisible. It read as "the selected row clips its meta line".
                                                    // Arbitrary font-size + `leading-5` (20px) keeps the ink inside the line
                                                    // box in every row state; rows without a description are untouched.
                                                    <span className={cn("block text-[12px] leading-5 mt-0.5 break-words", isSelected(option?.value) ? "text-primary-foreground/80" : "text-muted-foreground")}>
                                                        {option?.description}
                                                    </span>
                                                )}
                                            </span>
                                            {multiple && isSelected(option?.value) && (
                                                <Check className="h-4 w-4 shrink-0 mt-0.5" />
                                            )}
                                        </div>
                                    ))
                                )}
                            </div>
                        </Popover.Content>
                    </Popover.Portal>
                </Popover.Root>

                {/* Hidden native select for form submission */}
                <select
                    name={name}
                    value={value || ''}
                    onChange={() => { }} // Controlled by our custom logic
                    className="sr-only"
                    tabIndex={-1}
                    multiple={multiple}
                    required={required}
                    aria-hidden="true"
                >
                    <option value="">{t('Select...')}</option>
                    {options?.map(option => (
                        <option key={option?.value} value={option?.value}>
                            {option?.label}
                        </option>
                    ))}
                </select>
            </div>
            {description && !error && (
                <p className="text-sm text-muted-foreground mt-1">
                    {description}
                </p>
            )}
            {/* Programmatically linked to the trigger, not just visually adjacent: without an id +
                aria-describedby a screen-reader user hears the combobox and never the reason it was
                rejected. ui/Input already did this; Select did not. */}
            {error && (
                <p id={`${selectId}-error`} role="alert" className="text-sm text-destructive mt-1">
                    {error}
                </p>
            )}
        </div>
    );
});

Select.displayName = "Select";

export default Select;
